import type { ProjectOverviewResponse } from '@pulse/contracts';
import * as React from 'react';
import { Link, useParams } from 'react-router-dom';

import { AsOfPill } from '@/components/pulse/day';
import { ArrowIcon } from '@/components/pulse/icons';
import { Kpis } from '@/components/pulse/kpis';
import { PageHeader } from '@/components/pulse/page-header';
import { SourceLink } from '@/components/pulse/source-link';
import { useProjectOverview } from '@/domains/projects/api';
import { Meter } from '@/domains/projects/project-milestones-page';
import { ProjectTabs } from '@/domains/projects/project-tab';
import { useShell } from '@/domains/shell/api';

const CHIP: Readonly<Record<string, string>> = { 'On track': 'c-merged', 'At risk': 'c-warn', Late: 'c-changes', Done: 'c-approved', Closed: 'c-kind' };

/** A project's Overview (PRJ-8), built from the prototype's `viewProjectOverview()`. */
export function ProjectOverviewPage(): React.ReactElement {
  const { productSlug = '' } = useParams();
  const page = useProjectOverview(productSlug);
  const shell = useShell();
  return (
    <>
      <PageHeader
        eyebrow={`Project · ${page.data?.weekLabel ?? ''}`}
        title={page.data?.product.name ?? 'Project'}
        sub={page.data?.product.source ?? ''}
        right={<AsOfPill label={shell.data?.freshness.asOfLabel ?? null} />}
      />
      {page.isError ? <span className="sub">There is no project by that name.</span> : null}
      {page.data !== undefined ? <ProjectOverviewView data={page.data} /> : null}
    </>
  );
}

export function ProjectOverviewView({ data }: { readonly data: ProjectOverviewResponse }): React.ReactElement {
  const { product, tiles } = data;
  return (
    <>
      <ProjectTabs slug={product.slug} hasBoard={product.hasBoard} hasStories={product.hasStories} view="overview" />
      <Kpis
        items={[
          { label: 'Built this week', value: tiles.built, sub: 'Stories, tasks and PRs' },
          { label: 'Merged this week', value: tiles.merged, sub: 'Into dev and production' },
          { label: 'To production', value: tiles.toProduction, sub: tiles.toProduction > 0 ? 'This week' : 'Nothing reached production this week', warn: tiles.toProduction === 0 },
          tiles.waitingForAcceptance === null
            ? { label: 'Waiting for acceptance', value: '—', sub: 'No story repository' }
            : { label: 'Waiting for acceptance', value: tiles.waitingForAcceptance, sub: 'Stories in review', href: `/projects/${product.slug}/acceptance` },
        ]}
      />
      <div className="two" style={{ gridTemplateColumns: 'minmax(0,1fr) 360px', alignItems: 'start' }}>
        <div className="stack" style={{ gap: 16 }}>
          <section className="card pad stack" style={{ gap: 14 }}>
            <div className="row" style={{ justifyContent: 'space-between', gap: 12 }}>
              <h2>Milestones</h2>
              <Link className="linkish" to={`/projects/${product.slug}/milestones`} style={{ fontSize: 13 }}>
                All milestones <ArrowIcon />
              </Link>
            </div>
            {data.milestones.length === 0 ? (
              <span style={{ color: 'var(--muted)' }}>
                No open milestone yet. <Link to={`/projects/${product.slug}/milestones`}>Add one</Link>, and Talos fills it with the project&apos;s stories and tasks.
              </span>
            ) : null}
            {data.milestones.map((milestone) => (
              <div key={milestone.id} className="stack" style={{ gap: 6 }}>
                <div className="row" style={{ justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                  <span>
                    <b style={{ fontWeight: 600 }}>{milestone.title}</b> <span style={{ color: 'var(--muted)' }}>· {milestone.dateLabel}</span>
                  </span>
                  <span className="row" style={{ gap: 6 }}>
                    {milestone.source === 'Teamwork' ? <span className="chip c-kind">Teamwork</span> : null}
                    <span className={`chip ${CHIP[milestone.state] ?? 'c-kind'}`}>{milestone.state}</span>
                  </span>
                </div>
                <Meter done={milestone.done} total={milestone.total} state={milestone.state} />
                <span style={{ fontSize: 13, color: 'var(--muted)' }}>
                  {milestone.done} of {milestone.total} done · forecast {milestone.forecastLabel}
                  {milestone.talosLine !== null ? ` · Talos: ${milestone.talosLine}` : ''}
                </span>
              </div>
            ))}
          </section>
          <section className="card pad stack" style={{ gap: 12 }}>
            <div className="row" style={{ justifyContent: 'space-between', gap: 12 }}>
              <h2>This week</h2>
              {product.hasBoard ? (
                <Link className="linkish" to={`/projects/${product.slug}/board`} style={{ fontSize: 13 }}>
                  Today&apos;s board <ArrowIcon />
                </Link>
              ) : null}
            </div>
            {data.thisWeek.length > 0 ? (
              <ul className="plist">
                {data.thisWeek.map((item) => (
                  <li key={item.ref}>
                    <SourceLink href={item.url} style={{ color: 'inherit', textDecoration: 'none' }}>
                      {item.title}
                    </SourceLink>
                  </li>
                ))}
              </ul>
            ) : (
              <span style={{ color: 'var(--muted)' }}>Nothing built on it this week.</span>
            )}
          </section>
        </div>
        <aside className="stack" style={{ gap: 16 }}>
          <section className="card pad stack" style={{ gap: 12 }}>
            <h2>Who works on it</h2>
            {data.people.length === 0 ? <span style={{ color: 'var(--muted)' }}>Nobody built, merged or reviewed on it this week.</span> : null}
            {data.people.map((person) => (
              <Link key={person.userId} className="row" to={person.href} style={{ gap: 10, textDecoration: 'none', color: 'var(--ink)' }}>
                <span className="avatar sm" style={{ background: person.avatarColour }}>
                  {person.initials}
                </span>
                <span className="stack">
                  <span style={{ fontWeight: 600 }}>{person.name}</span>
                  <span style={{ fontSize: 12, color: 'var(--muted)' }}>{person.roleLine}</span>
                </span>
              </Link>
            ))}
          </section>
          <section className="card pad stack" style={{ gap: 8 }}>
            <h2>Risks</h2>
            <span style={{ color: 'var(--ink-2)' }}>
              {data.atRisk > 0 ? `${data.atRisk} ${data.atRisk === 1 ? 'item is' : 'items are'} still to do on at-risk or late milestones.` : 'Nothing at risk.'}
            </span>
          </section>
          <section className="card pad stack" style={{ gap: 8 }}>
            <h2>Latest meeting</h2>
            {data.latestMeeting !== null ? (
              <>
                <span style={{ fontWeight: 600 }}>
                  {data.latestMeeting.title} <span style={{ color: 'var(--muted)', fontWeight: 400 }}>· {data.latestMeeting.whenLabel}</span>
                </span>
                <span style={{ color: 'var(--ink-2)' }}>{data.latestMeeting.summary}</span>
                <Link className="linkish" to={`/projects/${product.slug}/meetings`} style={{ fontSize: 13 }}>
                  All meetings <ArrowIcon />
                </Link>
              </>
            ) : (
              <span style={{ color: 'var(--muted)' }}>
                No followed meeting has talked about {product.name} yet. <Link to="/projects/meetings">Meetings we follow</Link>
              </span>
            )}
          </section>
        </aside>
      </div>
    </>
  );
}

